import React, { useState } from "react";
import { directUrl, shortLink } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Eye, Copy, Check, Trash2, Globe, Lock, Link2 } from "lucide-react";

export default function ImageCard({ image, onClick, owner, onTogglePublic, onDelete }) {
  const [copied, setCopied] = useState(false);
  const src = directUrl(image);

  const copyShort = async (e) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(shortLink(image));
    setCopied(true);
    toast.success("Short link copied");
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      data-testid={`image-card-${image.short_code}`}
      onClick={() => onClick?.(image)}
      className="group relative rounded-xl overflow-hidden border border-border bg-card cursor-pointer break-inside-avoid mb-4 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
    >
      <div className="checker-bg">
        <img src={src} alt={image.title} loading="lazy" className="w-full object-cover" />
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

      <div className="absolute top-2.5 left-2.5 flex gap-1.5">
        <Badge variant="secondary" className="bg-black/55 text-white border-0 backdrop-blur-sm text-[11px]">
          <Eye className="h-3 w-3 mr-1" /> {image.views}
        </Badge>
        {image.source_type === "url" && (
          <Badge variant="secondary" className="bg-black/55 text-white border-0 backdrop-blur-sm text-[11px]">
            <Link2 className="h-3 w-3 mr-1" /> URL
          </Badge>
        )}
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <p className="text-white text-sm font-medium truncate mb-2">{image.title}</p>
        <div className="flex items-center gap-1.5">
          <button
            onClick={copyShort}
            data-testid={`copy-short-${image.short_code}`}
            className="h-8 px-2.5 rounded-md bg-white/15 backdrop-blur-sm text-white text-xs font-medium flex items-center gap-1.5 hover:bg-white/25 transition-colors"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} Copy link
          </button>
          {owner && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onTogglePublic?.(image);
                }}
                data-testid={`toggle-public-${image.short_code}`}
                className="h-8 px-2.5 rounded-md bg-white/15 backdrop-blur-sm text-white text-xs font-medium flex items-center gap-1.5 hover:bg-white/25 transition-colors"
              >
                {image.is_public ? <Globe className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                {image.is_public ? "Public" : "Private"}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.(image);
                }}
                data-testid={`delete-${image.short_code}`}
                className="h-8 w-8 rounded-md bg-white/15 backdrop-blur-sm text-white flex items-center justify-center hover:bg-destructive transition-colors"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
